import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Button, Result, Skeleton, Space, Typography, message} from 'antd';
import {ArrowLeftOutlined, CopyOutlined, QrcodeOutlined, ReloadOutlined} from '@ant-design/icons';
import styled from 'styled-components';
import api from '../../services/api';
import ENDPOINTS from '../../constants/endpoints';
import {formatPath, ROUTES} from '../../constants/PathRoutes';
import {BORDER_GRAY, GREE_1, WHITE} from '../../utils/colors';

const Section = styled.div`
    background: ${WHITE};
    border: 1px solid ${BORDER_GRAY};
    border-radius: 10px;
    padding: 20px 24px;
    margin-bottom: 16px;
`;

const QrWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 8px 0;
`;

const CopyCodeBox = styled.div`
    background: #f5f5f5;
    border: 1px solid ${BORDER_GRAY};
    border-radius: 8px;
    padding: 12px 16px;
    word-break: break-all;
    font-family: monospace;
    font-size: 12px;
    color: #555;
    max-width: 360px;
    width: 100%;
    text-align: center;
`;

function formatCountdown(seconds) {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
}

export default function ShopPixPayment() {
    const {id} = useParams();
    const navigate = useNavigate();

    const [pix, setPix] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [qrExpired, setQrExpired] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);

    const pollingRef = useRef(null);
    const countdownRef = useRef(null);

    const stopIntervals = useCallback(() => {
        clearInterval(pollingRef.current);
        clearInterval(countdownRef.current);
    }, []);

    const startPolling = useCallback(() => {
        clearInterval(pollingRef.current);
        pollingRef.current = setInterval(async () => {
            try {
                const {data: order} = await api.get(formatPath(ENDPOINTS.ECOMMERCE_ORDER_DETAIL, {id}));
                if (order.status === 'PAID') {
                    stopIntervals();
                    navigate(formatPath(ROUTES.LOJA_PEDIDO_DETAIL, {id}), {replace: true});
                }
            } catch (_) {
                // erros de polling são silenciosos
            }
        }, 5000);
    }, [id, navigate, stopIntervals]);

    const startCountdown = useCallback((expiresAt) => {
        clearInterval(countdownRef.current);

        const tick = () => {
            const diff = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
            setTimeLeft(diff);
            if (diff === 0) {
                clearInterval(countdownRef.current);
                clearInterval(pollingRef.current);
                setQrExpired(true);
            }
        };

        tick();
        countdownRef.current = setInterval(tick, 1000);
    }, []);

    const loadPix = useCallback(async () => {
        setLoading(true);
        setError(false);
        setQrExpired(false);
        stopIntervals();

        try {
            const {data} = await api.post(formatPath(ENDPOINTS.PAYMENTS_ORDER_PIX, {id}));
            setPix(data);

            if (data.is_expired) {
                setQrExpired(true);
            } else {
                startCountdown(data.expires_at);
                startPolling();
            }
        } catch (_) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [id, startCountdown, startPolling, stopIntervals]);

    useEffect(() => {
        loadPix();
        return stopIntervals;
    }, [loadPix, stopIntervals]);

    const handleCopy = () => {
        navigator.clipboard.writeText(pix.qr_code);
        message.success('Código copiado!');
    };

    const backLink = (
        <Button
            type="text"
            icon={<ArrowLeftOutlined/>}
            onClick={() => navigate(formatPath(ROUTES.LOJA_PEDIDO_DETAIL, {id}))}
            style={{padding: 0, color: GREE_1}}
        >
            Voltar ao Pedido
        </Button>
    );

    if (loading) {
        return (
            <>
                <Space style={{marginBottom: 20}}>{backLink}</Space>
                <Section>
                    <Skeleton active paragraph={{rows: 6}}/>
                </Section>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Space style={{marginBottom: 20}}>{backLink}</Space>
                <Section>
                    <Result
                        status="error"
                        title="Erro ao gerar QR Code"
                        subTitle="Não foi possível conectar ao serviço de pagamento. Tente novamente."
                        extra={
                            <Button
                                type="primary"
                                icon={<ReloadOutlined/>}
                                style={{background: GREE_1, borderColor: GREE_1}}
                                onClick={loadPix}
                            >
                                Tentar novamente
                            </Button>
                        }
                    />
                </Section>
            </>
        );
    }

    if (qrExpired) {
        return (
            <>
                <Space style={{marginBottom: 20}}>{backLink}</Space>
                <Section>
                    <Result
                        status="warning"
                        icon={<QrcodeOutlined style={{color: '#faad14'}}/>}
                        title="QR Code expirado"
                        subTitle="O prazo para pagamento expirou. Gere um novo código para continuar."
                        extra={
                            <Button
                                type="primary"
                                icon={<ReloadOutlined/>}
                                style={{background: GREE_1, borderColor: GREE_1}}
                                onClick={loadPix}
                            >
                                Gerar novo QR Code
                            </Button>
                        }
                    />
                </Section>
            </>
        );
    }

    const almostExpiring = timeLeft !== null && timeLeft < 60;

    return (
        <>
            <Space style={{marginBottom: 20}}>{backLink}</Space>

            <Section>
                <Space direction="vertical" size={2}>
                    <Typography.Title level={5} style={{margin: 0}}>
                        Pagamento via Pix
                    </Typography.Title>
                    <Typography.Text type="secondary" style={{fontSize: 13}}>
                        Escaneie o QR Code ou copie o código para pagar
                    </Typography.Text>
                </Space>
            </Section>

            <Section>
                <QrWrapper>
                    <img
                        src={`data:image/png;base64,${pix.qr_code_image_base64}`}
                        alt="QR Code Pix"
                        style={{width: 220, height: 220, borderRadius: 8}}
                    />

                    {timeLeft !== null && (
                        <Space direction="vertical" align="center" size={2}>
                            <Typography.Text type="secondary" style={{fontSize: 12}}>
                                Código válido por
                            </Typography.Text>
                            <Typography.Title
                                level={4}
                                style={{margin: 0, color: almostExpiring ? '#fa8c16' : GREE_1}}
                            >
                                {formatCountdown(timeLeft)}
                            </Typography.Title>
                        </Space>
                    )}

                    <Space direction="vertical" align="center" size={8} style={{width: '100%'}}>
                        <Typography.Text type="secondary" style={{fontSize: 12}}>
                            Ou use o código Pix Copia e Cola:
                        </Typography.Text>
                        <CopyCodeBox>{pix.qr_code}</CopyCodeBox>
                        <Button
                            icon={<CopyOutlined/>}
                            onClick={handleCopy}
                        >
                            Copiar código
                        </Button>
                    </Space>
                </QrWrapper>
            </Section>

            <Section>
                <Space direction="vertical" size={4}>
                    <Typography.Text strong>Como pagar:</Typography.Text>
                    <Typography.Text type="secondary">1. Abra o app do seu banco</Typography.Text>
                    <Typography.Text type="secondary">2. Escolha pagar com Pix</Typography.Text>
                    <Typography.Text type="secondary">3. Escaneie o QR Code ou cole o código</Typography.Text>
                    <Typography.Text type="secondary">4. Confirme o pagamento</Typography.Text>
                    <Typography.Text type="secondary" style={{marginTop: 8, display: 'block'}}>
                        A confirmação é automática e pode levar alguns segundos.
                    </Typography.Text>
                </Space>
            </Section>
        </>
    );
}
